import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Copy, ShareNetwork, DownloadSimple, Eye, WhatsappLogo, ArrowLeft, WifiHigh } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { useApi, responderUrl } from "../lib/api";
import { PageHeader } from "../components/common";
import { PRODUCTS, SWATCHES, useCart } from "../lib/site";

const TAG_IDS = ["id-card", "snap-band", "wide-band", "thin-line", "thin-kids", "mh-card"];

function ShopItem({ prod, add }) {
  const [colour, setColour] = useState(prod.colours[0]);
  return (
    <div className="panel p-3 flex flex-col" data-testid={`myqr-shop-${prod.id}`}>
      <div className="h-28 rounded-xl flex items-center justify-center p-2" style={{ background: prod.bg }}><img src={prod.imgs[colour]} alt={prod.name} className="max-h-full max-w-full object-contain" /></div>
      <div className="font-semibold text-sm mt-3">{prod.name}</div><div className="text-xs text-slate-400">{prod.desc}</div>
      <div className="flex gap-1.5 mt-2">{prod.colours.map((c) => <button key={c} title={c} onClick={() => setColour(c)} data-testid={`myqr-swatch-${prod.id}-${c}`}
        className={`w-5 h-5 rounded-full border border-slate-500 transition-transform ${colour === c ? "ring-2 ring-white scale-110" : ""}`} style={{ background: SWATCHES[c] }} />)}</div>
      <Button size="sm" className="mt-3" data-testid={`myqr-add-${prod.id}`} onClick={() => { add(prod.id, colour); toast.success(`${prod.name} (${colour}) added to cart`); }}>Add to cart</Button>
    </div>
  );
}

function MyQrShop({ p }) {
  const pet = p.kind === "pet";
  const { add, count } = useCart();
  const nav = useNavigate();
  const g = p.groups.find((x) => ["admin", "super"].includes(x.my_role)) || p.groups[0];
  const items = PRODUCTS.filter((x) => (pet ? x.id === "pet-tag" : TAG_IDS.includes(x.id)));
  return (
    <section className="mt-12" data-testid="myqr-shop">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div><h2 className="text-xl font-black">Put this QR on {pet ? "a collar tag" : "a card or band"}</h2>
          <p className="text-sm text-slate-400">Every item gets this same QR printed on it and written to its NFC chip.</p></div>
        <div className="flex gap-2"><Button variant="outline" asChild><Link to="/shop" data-testid="myqr-full-shop-link">Full shop</Link></Button>
          <Button className="bg-red-600 hover:bg-red-500 text-white" disabled={!count} data-testid="myqr-checkout-btn" onClick={() => nav(`/app/checkout?holder=${p.id}${g ? `&g=${g.id}` : ""}`)}>Checkout{count ? ` (${count})` : ""}</Button></div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">{items.map((x) => <ShopItem key={x.id} prod={x} add={add} />)}</div>
      <p className="text-xs text-slate-500 mt-3">At checkout, {pet ? "your pet" : "this person"} is picked as the holder so each item is linked to this QR.</p>
    </section>
  );
}

const QrCard = ({ name, url, qrRef, pet }) => (
  <div data-testid="my-qr-card" className="relative w-full max-w-[460px] aspect-[1.586] rounded-2xl bg-[#D7263D] text-white p-6 flex flex-col shadow-2xl fade-up">
    <div className="flex items-start justify-between">
      <div><div className="flex items-center gap-3"><img src="/landing/logo.webp" alt="" className="h-10 w-10 rounded-xl bg-white object-contain p-1" />
        <span className="text-3xl font-black tracking-tight">bsafe<span className="text-base font-medium">.today</span></span></div>
        <div className="mt-2 text-[10px] font-bold tracking-[0.35em]">EVERY SECOND COUNTS</div></div>
      <WifiHigh size={22} weight="bold" className="rotate-90" />
    </div>
    <div className="mt-auto flex items-end justify-between gap-4">
      <div className="text-sm leading-snug max-w-[55%]"><div className="text-lg font-black truncate" data-testid="my-qr-name">{name}</div>
        {pet ? <><b>Found me?</b> Tap with a phone, or scan the QR, to reach my owner.</> : <><b>Emergency?</b> Tap with a phone, or scan the QR, to see my medical info and next of kin.</>}</div>
      <div ref={qrRef} className="bg-white rounded-xl p-2 shrink-0"><QRCodeCanvas value={url} size={112} fgColor="#16294A" level="M" includeMargin={false} /></div>
    </div>
  </div>
);

export default function MyQR() {
  const { pid } = useParams();
  const api = useApi();
  const qrRef = useRef(null);
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/people/${pid}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [api, pid]);
  if (err) return <div className="text-slate-400" data-testid="my-qr-error">Could not load this profile. <Link className="underline" to="/app">Go back</Link></div>;
  if (!p) return <div className="text-slate-400">Loading…</div>;
  if (!p.personal_token) return <div className="text-slate-400" data-testid="my-qr-missing">Save the profile first to get your QR code. <Link className="underline" to={`/app/profile/${pid}`}>Back to profile</Link></div>;
  const name = `${p.first_name} ${p.is_minor ? "" : p.last_name || ""}`.trim();
  const url = responderUrl(p.personal_token, "link");
  const text = p.kind === "pet" ? `${name}'s bsafe.today found-pet page: ${url}` : `${name}'s bsafe.today emergency profile: ${url}`;
  const copy = async () => { await navigator.clipboard.writeText(url); toast.success("Link copied"); };
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ title: "bsafe.today", text, url }); } catch { /* cancelled */ } } else copy();
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = qrRef.current.querySelector("canvas").toDataURL("image/png");
    a.download = `bsafe-qr-${p.first_name.toLowerCase()}.png`; a.click();
  };
  return (
    <div data-testid="my-qr-page" className="max-w-5xl">
      <PageHeader overline="Your profile is live" title="My bsafe QR">
        <Button asChild variant="outline"><Link to={`/app/profile/${pid}`} data-testid="my-qr-back-btn"><ArrowLeft size={16} className="mr-1" />Back to profile</Link></Button>
      </PageHeader>
      <div className="grid lg:grid-cols-2 gap-10 items-start">
        <QrCard name={name} url={url} qrRef={qrRef} pet={p.kind === "pet"} />
        <div className="space-y-5">
          <p className="text-sm text-slate-300">This is your personal emergency link. It is the same QR and link that will be printed and written to the NFC chip of any bsafe card or band you order. Save it to your phone's lock screen or share it with family. If a card is reported lost, a new link is made and your other cards are re-written.</p>
          <div className="panel p-3 flex items-center gap-2"><code className="text-xs text-slate-300 truncate flex-1" data-testid="my-qr-link">{url}</code>
            <Button size="sm" variant="outline" onClick={copy} data-testid="my-qr-copy-btn"><Copy size={14} className="mr-1" />Copy</Button></div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={share} className="bg-red-600 hover:bg-red-500 text-white" data-testid="my-qr-share-btn"><ShareNetwork size={16} className="mr-1" />Share</Button>
            <Button asChild variant="outline"><a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" data-testid="my-qr-whatsapp-btn"><WhatsappLogo size={16} className="mr-1" />WhatsApp</a></Button>
            <Button variant="outline" onClick={download} data-testid="my-qr-download-btn"><DownloadSimple size={16} className="mr-1" />Download QR</Button>
            <Button asChild variant="outline"><a href={responderUrl(p.personal_token, "preview")} target="_blank" rel="noreferrer" data-testid="my-qr-preview-btn"><Eye size={16} className="mr-1" />View my emergency page</a></Button>
          </div>
          <p className="text-xs text-slate-500">Preview opens exactly what a responder sees. It sends no alerts and is not logged as a scan.</p>
        </div>
      </div>
      <MyQrShop p={p} />
    </div>
  );
}
